import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CompanyChecklist,
  CompanyContactCTA,
  CompanyPageHero,
  CompanyPageShell,
  DrugAssurance,
  DrugSources,
  useCompanyLang
} from "@/components/site/company/CompanyShared";
import { companyInquiryHref, sampleTypeCards } from "@/data/company";
import { PageMeta } from "@/components/site/PageMeta";

const ForetagDrogtestning = () => {
  const { lang, copy } = useCompanyLang();
  const d = copy.drogtestning;



  return (
    <CompanyPageShell breadcrumbLabel={copy.nav.drogtestning}>
      <PageMeta title={d.metaTitle} description={d.metaDescription} titleMaxLength={70} />
      <CompanyPageHero label={d.label} title={d.title}>
        <p>{d.subtitle}</p>
        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button asChild variant="hero" size="xl" className="w-full sm:w-auto">
            <a href={companyInquiryHref(lang, "drogtest-forebyggande")}>
              {copy.shared.quoteCta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button asChild variant="ghost-outline" size="xl" className="w-full sm:w-auto">
            <a href="#process">{d.processLink}</a>
          </Button>
        </div>
      </CompanyPageHero>

      <section className="pb-10">
        <div className="container-wide">
          <DrugAssurance />
        </div>
      </section>

      <section className="pb-14 lg:pb-16">
        <div className="container-wide">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {d.casesTitle}
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <article className="flex flex-col rounded-3xl border border-border/60 bg-card p-7 shadow-card">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                {d.casePreventiveEyebrow}
              </span>
              <h3 className="mt-3 font-display text-xl font-semibold text-foreground">{d.casePreventiveTitle}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{d.casePreventiveText}</p>
              <a
                href={companyInquiryHref(lang, "drogtest-forebyggande")}
                className="mt-5 inline-flex items-center text-sm font-medium text-primary"
              >
                {d.casePreventiveCta}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </a>
            </article>
            <a
              href="#nyanstallning"
              className="group flex flex-col rounded-3xl border border-border/60 bg-card p-7 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">{d.caseHireEyebrow}</span>
              <h3 className="mt-3 font-display text-xl font-semibold text-foreground">{d.caseHireTitle}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{d.caseHireText}</p>
              <span className="mt-5 inline-flex items-center text-sm font-medium text-primary">
                {d.caseHireCta}
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
            <a
              href="#vid-misstanke"
              className="group flex flex-col rounded-3xl border border-border/60 bg-card p-7 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                {d.caseSuspicionEyebrow}
              </span>
              <h3 className="mt-3 font-display text-xl font-semibold text-foreground">{d.caseSuspicionTitle}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{d.caseSuspicionText}</p>
              <span className="mt-5 inline-flex items-center text-sm font-medium text-primary">
                {d.caseSuspicionCta}
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </div>
        </div>
      </section>

      <section id="provtyper" className="scroll-mt-24 pb-14 lg:pb-16">
        <div className="container-wide">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary">{d.sampleEyebrow}</span>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {d.sampleTitle}
          </h2>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground">{d.sampleIntro}</p>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {sampleTypeCards.map((sample) => (
              <article
                key={sample.key}
                className="rounded-3xl border border-border/60 bg-card p-7 shadow-card"
              >
                <h3 className="font-display text-xl font-semibold text-foreground">{sample.title[lang]}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{sample.description[lang]}</p>
                <p className="mt-4 text-sm font-semibold text-foreground">{copy.shared.quotePrice}</p>
                <Button asChild variant="hero" size="sm" className="mt-5">
                  <a href={companyInquiryHref(lang, "drogtest-forebyggande", sample.key)}>
                    {sample.key === "saliv" ? d.sampleSalivCta : d.sampleUrinCta}
                  </a>
                </Button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="process" className="scroll-mt-24 pb-14 lg:pb-16">
        <div className="container-wide grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {d.processTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.processP1}</p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.processP2}</p>
            <a href="#process-detail" className="mt-5 inline-flex items-center text-sm font-medium text-primary">
              {d.processReadMore}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </a>
          </div>
          <aside className="rounded-3xl border border-border/60 bg-card p-7 shadow-card">
            <h2 className="font-display text-xl font-semibold text-foreground">{d.agreeTitle}</h2>
            <div className="mt-5">
              <CompanyChecklist items={d.agreeItems} />
            </div>
            <Button asChild variant="hero" size="lg" className="mt-6 w-full">
              <a href={companyInquiryHref(lang, "drogtest-forebyggande")}>{copy.shared.quoteCta}</a>
            </Button>
          </aside>
        </div>
      </section>

      <section id="nyanstallning" className="scroll-mt-24 pb-10">
        <div className="container-wide max-w-3xl">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">{d.hireSectionTitle}</h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.hireSectionText}</p>
          <Button asChild variant="ghost-outline" size="lg" className="mt-6">
            <a href={companyInquiryHref(lang, "drogtest-nyanstallning")}>{copy.shared.inquiryCta}</a>
          </Button>
        </div>
      </section>

      <section id="vid-misstanke" className="scroll-mt-24 pb-10">
        <div className="container-wide max-w-3xl">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            {d.suspicionSectionTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.suspicionSectionText}</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="hero" size="lg">
              <a href={companyInquiryHref(lang, "drogtest-misstanke")}>{copy.shared.inquiryCta}</a>
            </Button>
            <Button asChild variant="ghost-outline" size="lg">
              <a href="tel:+46317617700">
                <Phone className="mr-2 h-4 w-4" />
                {d.callCta}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section id="process-detail" className="scroll-mt-24 pb-14 lg:pb-16">
        <div className="container-wide">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {d.processDetailTitle}
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {d.processSteps.map((step) => (
              <article
                key={step.title}
                className="rounded-3xl border border-border/60 bg-card p-7 shadow-card"
              >
                <h3 className="font-display text-lg font-semibold text-foreground">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="container-wide max-w-3xl">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">{d.privacyTitle}</h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.privacyText}</p>
        </div>
      </section>

      <CompanyContactCTA service="drogtest-forebyggande" title={d.ctaTitle} />
      <DrugSources />
    </CompanyPageShell>
  );
};

export default ForetagDrogtestning;
